// Auto-generated article bodies for the Recovery Library catalog.
// Source: src/lib/library-articles.generated.json (produced by /tmp/gen_articles.py
// against Lovable AI). Reviewed and edited over time by the Recovery Trauma™ team.
import data from "./library-articles.generated.json";
import { LIBRARY_SLUG_ALIASES } from "./library-catalog";


export type GeneratedArticle = {
  title: string;
  category: string;
  markdown: string;
};

type RawArticle = GeneratedArticle | { title: string; category: string; error: string };

const ARTICLES = data as Record<string, RawArticle>;

const WORDS_PER_MIN = 220;

function wordCount(md: string): number {
  return md.replace(/```[\s\S]*?```/g, " ").split(/\s+/).filter(Boolean).length;
}

export function getGeneratedArticle(slug: string): GeneratedArticle | undefined {
  const a = ARTICLES[slug];
  if (a && "markdown" in a && a.markdown) return a;
  return undefined;
}

export function articleReadMin(slug: string): number | undefined {
  const a = getGeneratedArticle(slug);
  if (!a) return undefined;
  return Math.max(2, Math.round(wordCount(a.markdown) / WORDS_PER_MIN));
}

// Extract the first italic blurb or first paragraph of body text as a preview.
export function articleBlurb(slug: string): string | undefined {
  const a = getGeneratedArticle(slug);
  if (!a) return undefined;
  const md = a.markdown;
  // Try the italic blurb on the first non-empty content line after H1.
  const lines = md.split(/\r?\n/);
  let started = false;
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith("# ")) {
      started = true;
      continue;
    }
    if (!started) continue;
    if (line.startsWith("#")) break;
    // Strip leading > blockquote, bullets, emphasis markers, link wrappers.
    let text = line.replace(/^[>\-*+]\s*/, "");
    text = text.replace(/^\*+|\*+$/g, "").replace(/^_+|_+$/g, "");
    text = text.replace(/\[(.+?)\]\(.+?\)/g, "$1");
    text = text.replace(/`([^`]+)`/g, "$1");
    if (text.length > 220) text = text.slice(0, 217).trimEnd() + "…";
    if (text) return text;
  }
  return undefined;
}

export type ArticleMeta = {
  slug: string;
  title: string;
  category: string;
  readMin: number;
  blurb: string;
};

// Aliased slugs (e.g. the retired duplicate Somatic entries) are excluded so
// each topic is listed and searched exactly once.
export const GENERATED_ARTICLE_SLUGS: string[] = Object.keys(ARTICLES).filter(
  (s) => "markdown" in ARTICLES[s] && !(s in LIBRARY_SLUG_ALIASES),
);


export function listGeneratedArticles(): ArticleMeta[] {
  return GENERATED_ARTICLE_SLUGS.map((slug) => {
    const a = ARTICLES[slug] as GeneratedArticle;
    return {
      slug,
      title: a.title,
      category: a.category,
      readMin: articleReadMin(slug) ?? 5,
      blurb: articleBlurb(slug) ?? "",
    };
  });
}

// Synonym map for forgiving article search — mirrors the guides section so
// "coping", "DBT", "somatic" etc. surface related content across titles,
// categories and article body.
const ARTICLE_SYNONYMS: Record<string, string[]> = {
  coping: ["coping skills", "cope", "regulation", "grounding", "distress tolerance"],
  "coping skills": ["coping", "distress tolerance", "regulation", "grounding"],
  skills: ["coping", "coping skills", "tools"],
  dbt: ["dialectical", "distress tolerance", "emotion regulation", "mindfulness", "interpersonal effectiveness"],
  cbt: ["cognitive", "cognitive behavioural", "cognitive behavioral", "thought record", "reframe"],
  emdr: ["eye movement", "reprocessing", "trauma"],
  ifs: ["internal family systems", "parts work"],
  somatic: ["body", "nervous system", "polyvagal", "grounding"],
  trauma: ["ptsd", "cptsd", "complex trauma", "post traumatic"],
  ptsd: ["trauma", "post traumatic", "cptsd"],
  cptsd: ["complex trauma", "trauma", "ptsd"],
  anxiety: ["worry", "panic", "stress", "nervous"],
  depression: ["low mood", "sadness", "grief"],
  grief: ["loss", "bereavement", "mourning"],
  boundaries: ["limits", "assertiveness"],
  "self worth": ["self-worth", "selfworth", "self esteem", "self-esteem", "confidence", "worthiness"],
  "self-worth": ["self worth", "self esteem", "self-esteem", "confidence"],
  mindfulness: ["meditation", "present moment", "awareness"],
  triggers: ["trigger", "activation", "reaction"],
  "nervous system": ["polyvagal", "somatic", "vagus", "regulation"],
  "inner child": ["reparenting", "younger self"],
  attachment: ["bonding", "relational", "avoidant", "anxious"],
};

export function expandArticleTerms(q: string): string[] {
  const raw = q.trim().toLowerCase();
  if (!raw) return [];
  const tokens = raw.replace(/[^\p{L}\p{N}\s-]/gu, " ").split(/\s+/).filter((t) => t.length > 1);
  const terms = new Set<string>();
  terms.add(raw);
  for (const t of tokens) terms.add(t);
  if (ARTICLE_SYNONYMS[raw]) ARTICLE_SYNONYMS[raw].forEach((s) => terms.add(s.toLowerCase()));
  for (const t of tokens) {
    if (ARTICLE_SYNONYMS[t]) ARTICLE_SYNONYMS[t].forEach((s) => terms.add(s.toLowerCase()));
  }
  return Array.from(terms);
}

export function searchArticleSlugs(query: string): Set<string> {
  const terms = expandArticleTerms(query);
  if (terms.length === 0) return new Set(GENERATED_ARTICLE_SLUGS);
  const out = new Set<string>();
  for (const slug of GENERATED_ARTICLE_SLUGS) {
    const a = ARTICLES[slug] as GeneratedArticle;
    const hay = `${a.title} ${a.category} ${a.markdown}`.toLowerCase();
    // Match if ANY expanded term is a substring — forgiving partial + synonym match.
    if (terms.some((t) => hay.includes(t))) out.add(slug);
  }
  return out;
}
